import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';

@Component({
  selector: 'app-category2-question3',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    PlusComponent,
    DropdownComponent,
    TrashComponent
  ],
  template: `
    <div class="text-[13px]">
      {{ strings[language].question }}
    </div>
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>
    <br />
    <div class="flex flex-wrap gap-4 sm:gap-6">
      <div
        (click)="usage = true; onKeyUp()"
        class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
      >
        <div
          [ngClass]="{ 'bg-[#183A25]': usage }"
          class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
        ></div>
        <div>{{ strings[language].choice_yes }}</div>
      </div>
      <div>
        <div
          (click)="usage = false; onKeyUp()"
          class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
        >
          <div
            [ngClass]="{
              'bg-[#183A25]': !usage
            }"
            class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
          ></div>
          <div>{{ strings[language].choice_no }}</div>
        </div>
      </div>
    </div>

    <div *ngIf="usage">
      <div class="bg-[#043B22] flex px-3 py-2.5 rounded-t-lg gap-4 mt-6 items-center">
        <div class="text-white text-[13px] flex-1"></div>
        <div class="text-white text-[13px] flex-1"></div>
        <div
          (click)="createForm()"
          class="w-6 h-6 flex justify-center items-center cursor-pointer bg-white/20 hover:bg-white/30 rounded-full transition-colors"
        >
          <app-plus color="white"></app-plus>
        </div>
      </div>
      <div
        *ngFor="let energy of energyTypeControls; let x = index"
        class="flex py-4 px-1 border-y border-[#DEE2E6] gap-5"
      >
        <div class="w-1/2">
          <app-dropdown
            placeholder="{{ strings[language].select }}"
            [options]="[
              { value: 'heat', label: this.strings[this.language].heat },
              {
                value: 'steam',
                label: this.strings[this.language].steam
              },
              {
                value: 'biogas',
                label: this.strings[this.language].bio
              }
            ]"
            [control]="energy.row['type']"
          ></app-dropdown>
        </div>
        <div class="w-1/2">
          <app-input
            [keyUp]="onKeyUp"
            type="number"
            [dollar]="false"
            unit="kWh"
            placeholder="0.00"
            [control]="energy.row['energy']"
          >
          </app-input>
        </div>
        <div
          (click)="removeItem(x); onKeyUp()"
          class="w-1/9 flex justify-end pl-1 align-middle items-center cursor-pointer"
        >
          <app-trash></app-trash>
        </div>
      </div>
    </div>
  `
})
export class Category2Question3Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();

  usage = false;

  energyTypeControls = [
    {
      row: {
        type: new FormControl('', Validators.required),
        energy: new FormControl('', Validators.required)
      }
    }
  ];

  removeItem(index: number) {
    this.energyTypeControls.splice(index, 1);
  }
  form = new FormGroup({
    rows: new FormControl(this.energyTypeControls)
  });

  strings: any = {
    english: {
      question: 'Do you draw import other forms of energy?',
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      energy_type: 'energy type',
      select: 'Select',
      heat: 'Heat',
      steam: 'Steam',
      bio: 'Biogas'
    },
    italian: {
      question: 'Importi altre forme di energia?',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      energy_type: 'tipo di energia',
      select: 'Seleziona',
      heat: 'Calore',
      steam: 'Vapore',
      bio: 'Biogas'
    }
  };

  ngOnInit() {
    let categories = localStorage.getItem('categories');

    if (categories) {
      categories = JSON.parse(categories);

      // @ts-ignore
      const savedAnswer = categories?.[1]?.questions?.[2]?.answers;

      if (savedAnswer && typeof savedAnswer === 'object') {
        this.usage = savedAnswer.usage ?? false;

        const answers = savedAnswer.data;
        if (Array.isArray(answers) && answers.length > 0) {
          let newControls = answers.map((ex: any) => ({
            row: {
              type: new FormControl(ex.type, Validators.required),
              energy: new FormControl(ex.energy, Validators.required)
            }
          }));

          this.energyTypeControls = newControls;
          this.form.setValue({ rows: newControls });
        }
      }
    }
  }

  createForm = () => {
    this.energyTypeControls.push({
      row: {
        type: new FormControl('', Validators.required),
        energy: new FormControl('', Validators.required)
      }
    });
  };
  energyTypeLabels: any = {
    heat: { english: 'Heat', italian: 'Calore' },
    steam: { english: 'Steam', italian: 'Vapore' },
    biogas: { english: 'Biogas', italian: 'Biogas' }
  };

  onKeyUp = async () => {
    try {
      let newAnswers = this.form.value.rows?.map((row: any) => {
        const typeValue = row.row.type.value;
        return {
          type: typeValue,
          energy: row.row.energy.value,
          label: {
            english: this.energyTypeLabels[typeValue]?.english || '',
            italian: this.energyTypeLabels[typeValue]?.italian || ''
          }
        };
      });

      const answerPayload = {
        usage: this.usage,
        data: newAnswers
      };

      // @ts-ignore
      this.categories[1].questions[2].answers = answerPayload;
      this.categoryService.persistCategories(this.categories);
    } catch (error) {
      console.error(error);
    }
  };
}
